/**
 * Configuration updates for furniture selection.
 * Placement coordinates stay on the slab; this only writes product ids and
 * the named `#hardware-guides` id they attach to.
 *
 * Classic escutcheons pair with a bar handle. Finger-pull escutcheons replace
 * the external handle and do not require one.
 */

import type {
  DoorHardwareConfiguration,
  DoorHardwareSelection,
} from '../types/doorConfiguration';
import type { HardwareFace } from './hardwareCompatibility';
import {
  getHardwareProduct,
  hardwareProductsByFamily,
  type HardwareColourFamilyId,
  type HardwareProduct,
} from './hardwareCatalog';

function operatingHandleIds(): Set<string> {
  return new Set(
    [...hardwareProductsByFamily('lever'), ...hardwareProductsByFamily('pull-bar')].map(
      (product) => product.id
    )
  );
}

function lockingProductIds(): Set<string> {
  return new Set(hardwareProductsByFamily('locking').map((product) => product.id));
}

function lockingIdsByRole(role: HardwareProduct['role']): Set<string> {
  return new Set(
    hardwareProductsByFamily('locking')
      .filter((product) => product.role === role)
      .map((product) => product.id)
  );
}

function fingerPullIds(): Set<string> {
  return lockingIdsByRole('finger-pull-escutcheon');
}

function classicEscutcheonIds(): Set<string> {
  return lockingIdsByRole('escutcheon');
}

function leverIds(): Set<string> {
  return new Set(hardwareProductsByFamily('lever').map((product) => product.id));
}

function letterplateIds(): Set<string> {
  return new Set(hardwareProductsByFamily('letterplate').map((product) => product.id));
}

export function withOperatingHandle(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace,
  selection: DoorHardwareSelection | null
): DoorHardwareConfiguration {
  const operatingIds = operatingHandleIds();
  const product = selection ? getHardwareProduct(selection.id) : undefined;
  let kept = hardware[face].filter((item) => !operatingIds.has(item.id));
  if (product?.family === 'lever') {
    const exclusiveLockingIds = new Set([...fingerPullIds(), ...classicEscutcheonIds()]);
    kept = kept.filter((item) => !exclusiveLockingIds.has(item.id));
  } else if (product?.family === 'pull-bar') {
    const keepLocking = classicEscutcheonIds();
    kept = kept.filter(
      (item) => !lockingProductIds().has(item.id) || keepLocking.has(item.id)
    );
  }
  return {
    ...hardware,
    [face]: selection ? [...kept, selection] : kept,
  };
}

export function selectedOperatingHandleId(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace = 'external'
): string | undefined {
  const operatingIds = operatingHandleIds();
  return hardware[face].find((item) => operatingIds.has(item.id))?.id;
}

export function withLockingMechanism(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace,
  selection: DoorHardwareSelection | null
): DoorHardwareConfiguration {
  const lockingIds = lockingProductIds();
  let kept = hardware[face].filter((item) => !lockingIds.has(item.id));
  const product = selection ? getHardwareProduct(selection.id) : undefined;
  if (product?.role === 'finger-pull-escutcheon') {
    const operatingIds = operatingHandleIds();
    kept = kept.filter((item) => !operatingIds.has(item.id));
  }
  if (product?.role === 'escutcheon') {
    kept = kept.filter((item) => !leverIds().has(item.id));
  }
  return {
    ...hardware,
    [face]: selection ? [...kept, selection] : kept,
  };
}

export function selectedLockingMechanism(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace = 'external'
): DoorHardwareSelection | undefined {
  const lockingIds = lockingProductIds();
  return hardware[face].find((item) => lockingIds.has(item.id));
}

export function selectedLockingMechanismId(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace = 'external'
): string | undefined {
  return selectedLockingMechanism(hardware, face)?.id;
}

export function withLetterplate(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace,
  selection: DoorHardwareSelection | null
): DoorHardwareConfiguration {
  const ids = letterplateIds();
  const kept = hardware[face].filter((item) => !ids.has(item.id));
  return {
    ...hardware,
    [face]: selection ? [...kept, selection] : kept,
  };
}

export function selectedLetterplate(
  hardware: DoorHardwareConfiguration,
  face: HardwareFace = 'external'
): DoorHardwareSelection | undefined {
  const ids = letterplateIds();
  return hardware[face].find((item) => ids.has(item.id));
}

/** Cycle a product across named slab placements, then clear. Coordinates stay on the slab. */
export function nextCycledHardwareSelection(
  productId: string,
  placementIds: string[],
  current: DoorHardwareSelection | undefined
): DoorHardwareSelection | null {
  if (placementIds.length === 0) return null;
  if (!current || current.id !== productId) {
    return { id: productId, placementId: placementIds[0] };
  }
  const index = placementIds.findIndex((id) => id === current.placementId);
  const nextId = placementIds[index + 1];
  return nextId ? { id: productId, placementId: nextId } : null;
}

/** Cycle a letterplate across slab zones, then clear. Coordinates stay on the slab. */
export function nextLetterplateSelection(
  product: HardwareProduct,
  zones: Array<{ id: string }>,
  current: DoorHardwareSelection | undefined
): DoorHardwareSelection | null {
  return nextCycledHardwareSelection(
    product.id,
    zones.map((zone) => zone.id),
    current
  );
}

/** Cycle a bar escutcheon across standard and heritage lock points, then clear. */
export function nextEscutcheonSelection(
  product: HardwareProduct,
  placementIds: string[],
  current: DoorHardwareSelection | undefined
): DoorHardwareSelection | null {
  return nextCycledHardwareSelection(product.id, placementIds, current);
}

export function withColourFamily(
  hardware: DoorHardwareConfiguration,
  colourFamilyId: HardwareColourFamilyId
): DoorHardwareConfiguration {
  if (hardware.colourFamilyId === colourFamilyId) return hardware;
  let next = { ...hardware, colourFamilyId };
  const handle = getHardwareProduct(selectedOperatingHandleId(next));
  if (handle?.colourFamilyId && handle.colourFamilyId !== colourFamilyId) {
    next = withOperatingHandle(next, 'external', null);
  }
  const locking = getHardwareProduct(selectedLockingMechanismId(next));
  if (locking?.colourFamilyId && locking.colourFamilyId !== colourFamilyId) {
    next = withLockingMechanism(next, 'external', null);
  }
  const letterplate = getHardwareProduct(selectedLetterplate(next)?.id);
  if (letterplate?.colourFamilyId && letterplate.colourFamilyId !== colourFamilyId) {
    next = withLetterplate(next, 'external', null);
  }
  return next;
}

export function furniturePairingHint(
  hardware: DoorHardwareConfiguration
): string | null {
  const handle = getHardwareProduct(selectedOperatingHandleId(hardware));
  const locking = getHardwareProduct(selectedLockingMechanismId(hardware));
  if (handle?.family === 'pull-bar' && locking?.role !== 'escutcheon') {
    return 'A bar handle needs an escutcheon.';
  }
  if (locking?.role === 'escutcheon' && handle?.family !== 'pull-bar') {
    return 'An escutcheon needs a bar handle.';
  }
  return null;
}

/**
 * Why this product cannot be chosen with the current furniture, or null if it can.
 * Slab placement is checked separately.
 */
export function hardwareProductPairingReason(
  product: HardwareProduct,
  hardware: DoorHardwareConfiguration
): string | null {
  const handle = getHardwareProduct(selectedOperatingHandleId(hardware));
  const locking = getHardwareProduct(selectedLockingMechanismId(hardware));
  if (product.family === 'lever' && locking?.role === 'escutcheon') {
    return 'An escutcheon needs a bar handle.';
  }
  if (product.role === 'rim-cylinder' && handle?.family === 'pull-bar') {
    return 'A bar handle needs an escutcheon.';
  }
  return null;
}
